import { useEffect, useMemo, useRef } from 'react';
import { matchPath, useLocation, useNavigate } from 'react-router';
import { useConsultationPatientId, usePatientSync } from './PatientSyncProvider';

const PATIENT_OVERVIEW_ROUTE = '/patient-overview/:medatixxPatientId';
const RECORDING_ROUTE = '/recording/:medatixxPatientId?';
const RESULT_ROUTE = '/result/:id';
const CONSULTATION_OVERVIEW_ROUTES = ['/', '/home'];

function describeRoute(pathname: string) {
  const recording = matchPath(RECORDING_ROUTE, pathname);
  return {
    isConsultationActive: recording !== null || matchPath(RESULT_ROUTE, pathname) !== null,
    isOnOverview: CONSULTATION_OVERVIEW_ROUTES.includes(pathname),
    patientId:
      matchPath(PATIENT_OVERVIEW_ROUTE, pathname)?.params.medatixxPatientId ??
      recording?.params.medatixxPatientId ??
      null,
  };
}

export function PatientSyncNavigator() {
  const { activePatient, reportLocation } = usePatientSync();
  const consultationPatientId = useConsultationPatientId();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const route = useMemo(() => describeRoute(pathname), [pathname]);
  const shownPatientId =
    route.patientId ?? (route.isConsultationActive ? consultationPatientId : null);
  const shownPatientIdRef = useRef(shownPatientId);
  shownPatientIdRef.current = shownPatientId;
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  useEffect(() => {
    reportLocation({
      isConsultationActive: route.isConsultationActive,
      isOnOverview: route.isOnOverview,
      shownPatientId,
    });
  }, [route, shownPatientId, reportLocation]);

  useEffect(() => {
    if (!activePatient || shownPatientIdRef.current === activePatient.patientId) return;
    void navigateRef.current(`/patient-overview/${encodeURIComponent(activePatient.patientId)}`);
  }, [activePatient]);

  return null;
}
